export namespace wimi.extranet {
    export const version = "2.0.0.1";
    export const protocols = Object.freeze(["wimi-extranet-v2.1", "wimi-extranet-v2.0", "wimi-extranet-v1.0"]);
    export const websocket_service_url = "/wimi.extranet.local.service?client-version=" + version;

    const kmax_reconnect_interval = 15000;
    const kmin_reconnect_interval = 100;
    const krequest_sequence_chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const global: any = window;

    export interface IMessage {
        cmd: string,
        sequence: string,
        timestamp: number,
    }

    export interface IRequestMessage<TArgs> extends IMessage {
        args: TArgs
    }

    export interface IResponseMessage<TResult> extends IMessage {
        result: TResult
    }

    export type ResponseError = {
        ec: number,
        message: string
    };

    export enum ErrorCodes {
        NotReady = 0x01,
        Timeout = 0x02,
        ConnectionDisconnected = 0x03
    }

    export type ServiceEventTypes = "ServiceConnected" | "ServiceDisconnected" | "ChannelDisconnected" | "LogOutput" | "HeartbeatMessage";
    export type ServiceWorkStatus = "Working" | "Ready" | "Fault";
    export type DiagnosisHandler = (time: Date, result: ChannelDiagnosisResult) => void;

    type TransactionHandler<TResult> = {
        resolve: (response: IResponseMessage<TResult>) => void,
        reject: (ec: number, message: string) => void;
    };

    class TransactionHandlerMap {
        private _data: any;
        public constructor() {
            if (global.Map)
                this._data = new global.Map();
            else
                this._data = {};
        }

        public tryPop<TResult>(sequence: string): TransactionHandler<TResult> | undefined {
            let callback;
            if (global.Map) {
                callback = this._data.get(sequence);
                this._data.delete(sequence);
            } else {
                callback = this._data[sequence];
                delete this._data[sequence];
            }
            return callback;
        }

        public tryPeek<TResult>(sequence: string): TransactionHandler<TResult> | undefined {
            let callback;
            if (global.Map) {
                callback = this._data.get(sequence);
            } else {
                callback = this._data[sequence];
            }
            return callback;
        }

        public remove(sequence: string): void {
            if (global.Map) {
                this._data.delete(sequence);
            } else {
                delete this._data[sequence];
            }
        }

        public push<TResult>(sequence: string, handler: TransactionHandler<TResult>): void {
            if (global.Map) {
                this._data.set(sequence, handler);
            } else {
                this._data[sequence] = handler;
            }
        }

        public popAll(): Array<TransactionHandler<any>> {
            let results = [];
            if (global.Map) {
                for (const value of this._data.values()) {
                    results.push(value)
                }
                this._data.clear();
            } else {
                for (const key in this._data) {
                    results.push(this._data[key]);
                }
                this._data = {};
            }
            return results;
        }
    }

    class UTF8Decoder {
        private _ESDecoder: any = null;
        public constructor() {
            if ("TextDecoder" in global) {
                this._ESDecoder = new global.TextDecoder("utf-8");
            }
        }

        public decode(buffer: ArrayBuffer): string {
            if (this._ESDecoder) {
                return this._ESDecoder.decode(buffer);
            }
            let out, i, len, c;
            let char2, char3;

            const arr = new Uint8Array(buffer);
            out = "";
            len = arr.length;
            i = 0;
            while (i < len) {
                c = arr[i++];
                switch (c >> 4) {
                    case 0:
                    case 1:
                    case 2:
                    case 3:
                    case 4:
                    case 5:
                    case 6:
                    case 7:
                        // 0xxxxxxx
                        out += String.fromCharCode(c);
                        break;
                    case 12:
                    case 13:
                        // 110x xxxx   10xx xxxx
                        char2 = arr[i++];
                        out += String.fromCharCode(((c & 0x1F) << 6) | (char2 & 0x3F));
                        break;
                    case 14:
                        // 1110 xxxx  10xx xxxx  10xx xxxx
                        char2 = arr[i++];
                        char3 = arr[i++];
                        out += String.fromCharCode(((c & 0x0F) << 12) |
                            ((char2 & 0x3F) << 6) |
                            ((char3 & 0x3F) << 0));
                        break;
                }
            }
            return out;
        }
    }

    class UTF8Encoder {
        private _ESEncoder: any = null;
        public constructor() {
            if ("TextEncoder" in global) {
                this._ESEncoder = new global.TextEncoder("utf-8");
            }
        }

        public encode(str: string) {
            if (!str)
                return new Uint8Array(0);
            if (typeof (str) !== "string")
                str = JSON.stringify(str);

            if (this._ESEncoder) {
                return this._ESEncoder.encode(str);
            }

            var bytes = new Array();
            var len, c;
            len = str.length;
            for (var i = 0; i < len; i++) {
                c = str.charCodeAt(i);
                if (c >= 0x010000 && c <= 0x10FFFF) {
                    bytes.push(((c >> 18) & 0x07) | 0xF0);
                    bytes.push(((c >> 12) & 0x3F) | 0x80);
                    bytes.push(((c >> 6) & 0x3F) | 0x80);
                    bytes.push((c & 0x3F) | 0x80);
                } else if (c >= 0x000800 && c <= 0x00FFFF) {
                    bytes.push(((c >> 12) & 0x0F) | 0xE0);
                    bytes.push(((c >> 6) & 0x3F) | 0x80);
                    bytes.push((c & 0x3F) | 0x80);
                } else if (c >= 0x000080 && c <= 0x0007FF) {
                    bytes.push(((c >> 6) & 0x1F) | 0xC0);
                    bytes.push((c & 0x3F) | 0x80);
                } else {
                    bytes.push(c & 0xFF);
                }
            }
            return new Uint8Array(bytes);
        }
    }


    export class ServiceEvent implements Event {
        public bubbles: boolean = false;
        public cancelBubble: boolean = false;
        public cancelable: boolean = false;
        public composed: boolean = false;
        public currentTarget: EventTarget | null = null;
        public defaultPrevented: boolean = false;
        public eventPhase: number = 0;
        public isTrusted: boolean = false;
        public returnValue: boolean = false;
        public srcElement: EventTarget | null = null;
        public target: EventTarget | null = null;
        public timeStamp: number = new Date().getTime();
        public type: string = "";
        public stopImmediate: boolean = false;
        public composedPath(): EventTarget[] {
            return []
        }
        public initEvent(type: string, bubbles?: boolean, cancelable?: boolean): void {
            this.type = type;
            this.bubbles = bubbles ? true : false;
            this.cancelable = cancelable ? true : false;
        }

        public preventDefault(): void {
        }
        public stopImmediatePropagation(): void {
            this.stopImmediate = true;
        }
        public stopPropagation(): void {
        }
        public AT_TARGET: number = 0;
        public BUBBLING_PHASE: number = 0;
        public CAPTURING_PHASE: number = 0;
        public NONE: number = 0;
    }

    export class ChannelStatusEvent extends ServiceEvent {
        public connectCode: string = "";
        public channelId: number = 0;
        public constructor() {
            super();
        }
    }

    export class ServiceConnectedEvent extends ServiceEvent {
        public serverInfo!: ServiceInfo;
        public constructor() {
            super();
        }
    }

    export enum LogLevel {
        info = 0,
        debug = 1,
        warning = 2,
        error = 3
    }

    export class LogOutputEvent extends ServiceEvent {
        public level: LogLevel = LogLevel.info;
        public source: string = "";
        public message: string = "";
        public constructor() {
            super();
        }
    }

    export class HeartbeatMessageEvent extends ServiceEvent {
        public data!: ServiceInfo;
        public constructor() {
            super();
        }
    }

    export type ChannelLinkInfo = {
        virtual_address: string,
        gw_lan_address: string,
        td_lan_address: string,
        options: string
    }

    export type ChannelInfo = {
        connect_code: string,
        device_code: string,
        device_name: string,
        tux_address: string,
        gw_address: string,
        options: string,
        expires: number,
        try_connect_counter: number,
        status: string,
        links: Array<ChannelLinkInfo>
    }

    export type ChannelConnectResult = {
    
        status: number,
        extranetServerStatus: ServiceWorkStatus,
        message: string,
        channel: ChannelInfo
    }

    export type ChannelDisconnectResult = {
        status: number,
        extranetServerStatus: ServiceWorkStatus,
        message: string
    }

    export type ChannelDiagnosisResult = {
        connect_code: string,
        node: "local_interface" | "network_server" | "gateway_device" | "target_lan_device" | "target_virtual_device" | "unknown",
        step: "begin" | "in_progress" | "completed" | "unknown",
        code: number,
        message: string,
        address: string,
        packet_loss: number,
        average_latency: number,
        finish: boolean,
    }

    export type ServiceInfo = {
        name: string,
        version: string,
        buildTime: string,
        extranetServerAddress: string,
        extranetServerStatus: ServiceWorkStatus,
        sessionId: string,
        clientId: string,
        hostname: string,
        locale: string
    }

    export class Service implements EventTarget {
        private _connected = false;
        private _protocol = "";
        private _socket: WebSocket | null = null;
        private _reconnect_timeout = kmin_reconnect_interval;
        private _user_closed = false;
        private _request_sequence_index = 0;
        private _request_transactions = new TransactionHandlerMap();
        private _listeners: any = {};
        private _heartbeatTimer: number = -1;
        private _wsBaseUrl: string;
        constructor(wsBaseUrl:string) {
            this._wsBaseUrl = wsBaseUrl;
        }

        public get connected(): boolean { return this._connected; }

        public get protocol(): string { return this._protocol; }

        public requestTimeout: number = 10000;

        public initialize(): void {
            this._socket = this._websocket_init();
        }

        public shutdown(): void {
            this._user_closed = true;
            if (this._socket) {
                try { this._socket.close(); } catch (ex) { }
                this._socket = null;
            }
        }

        public waitConnected(): Promise<void> {
            return new Promise<void>((resolve) => {
                if (this.connected)
                    resolve();
                const connectedListener = (ev: Event) => {
                    this.removeEventListener("ServiceConnected", connectedListener);
                    resolve();
                };

                this.addEventListener("ServiceConnected", connectedListener);
            });
        }

        public addEventListener(type: ServiceEventTypes, callback: EventListenerOrEventListenerObject | null, options?: boolean | AddEventListenerOptions): void {
            if (!(type in this._listeners)) {
                this._listeners[type] = [];
            }
            this._listeners[type].push(callback);
        }

        public dispatchEvent(event: Event): boolean {
            if (!(event.type in this._listeners)) {
                return false;
            }
            var stack = this._listeners[event.type];
            (event as any).target = this;
            for (var i = 0, l = stack.length; i < l; i++) {
                stack[i].call(this, event);
            }
            return true;
        }

        public removeEventListener(type: ServiceEventTypes, callback: EventListenerOrEventListenerObject | null, options?: boolean | EventListenerOptions): void {
            if (!(type in this._listeners)) {
                return;
            }
            var stack = this._listeners[type];
            for (var i = 0, l = stack.length; i < l; i++) {
                if (stack[i] === callback) {
                    stack.splice(i, 1);
                    return this.removeEventListener(type, callback);
                }
            }
        }

        public ChannelConnect(connectCode: string): Promise<ChannelConnectResult> {
            return new Promise<ChannelConnectResult>(async (resolve, reject) => {
                try {
                    let message = this._make_request_message("ChannelConnectRequest", { connect_code: connectCode });
                    let response = await this._request_service<ChannelConnectResult>(message);
                    resolve(response.result);
                } catch (ex) {
                    reject(ex);
                }
            });
        }

        public ChannelDisconnect(connectCode: string): Promise<ChannelDisconnectResult> {
            return new Promise<ChannelDisconnectResult>(async (resolve, reject) => {
                try {
                    let message = this._make_request_message("ChannelDisconnectRequest", { connect_code: connectCode });
                    let response = await this._request_service<ChannelDisconnectResult>(message);
                    resolve(response.result);
                } catch (ex) {
                    reject(ex);
                }
            });
        }

        public ChannelDiagnosis(connectCode: string, handler: DiagnosisHandler): Promise<void> {
            return new Promise<void>(async (resolve, reject) => {
                try {
                    let message = this._make_request_message("ChannelDiagnosisRequest", { connect_code: connectCode });
                    if (this.connected) {
                        if (this._socket) {
                            this._socket.send(JSON.stringify(message));
                            this._request_transactions.push(message.sequence, {
                                resolve: function (response) {
                                    const result = response.result as any;
                                    handler(new Date(response.timestamp), result);
                                    if (result.finish)
                                        resolve();
                                },
                                reject: function (ec, message) {
                                    reject({
                                        ec: ec,
                                        message: message
                                    });
                                }
                            });
                        }
                    }
                } catch (ex) {
                    reject(ex);
                }
            });
        }

        public ChannelsList(): Promise<Array<ChannelInfo>> {
            return new Promise<Array<ChannelInfo>>(async (resolve, reject) => {
                try {
                    let message = this._make_request_message("ChannelsListRequest");
                    let response = await this._request_service<Array<ChannelInfo>>(message);
                    resolve(response.result);
                } catch (ex) {
                    reject(ex);
                }
            });
        }

        public SessionDataSet(key: string, value: any): Promise<boolean> {
            if (typeof (value) !== "string")
                value = JSON.stringify(value);

            return new Promise<boolean>(async (resolve, reject) => {
                try {
                    let message = this._make_request_message("SessionDataRequest", { key: key, method: "put", value: value });
                    let response = await this._request_service<boolean>(message);
                    resolve(response.result);
                } catch (ex) {
                    reject(ex);
                }
            });
        }

        public SessionDataGet(key: string): Promise<string> {
            return new Promise<string>(async (resolve, reject) => {
                try {
                    let message = this._make_request_message("SessionDataRequest", { key: key, method: "get" });
                    let response = await this._request_service<string>(message);
                    resolve(response.result);
                } catch (ex) {
                    reject(ex);
                }
            });
        }

        public SessionDataDel(key: string): Promise<boolean> {
            return new Promise<boolean>(async (resolve, reject) => {
                try {
                    let message = this._make_request_message("SessionDataRequest", { key: key, method: "del" });
                    let response = await this._request_service<boolean>(message);
                    resolve(response.result);
                } catch (ex) {
                    reject(ex);
                }
            });
        }

        public LoggerStateSet(state: boolean): Promise<boolean> {
            return new Promise<boolean>(async (resolve, reject) => {
                try {
                    let message = this._make_request_message("LoggerStateSetRequest", { state: state });
                    let response = await this._request_service<boolean>(message);
                    resolve(response.result);
                } catch (ex) {
                    reject(ex);
                }
            });
        }

        public GetServiceInfo(): Promise<ServiceInfo> {
            return new Promise<ServiceInfo>(async (resolve, reject) => {
                try {
                    let message = this._make_request_message("HeartbeatRequest");
                    let response = await this._request_service<ServiceInfo>(message);
                    resolve(response.result);
                } catch (ex) {
                    reject(ex);
                }
            });
        }

        private _generate_request_sequence_string(): string {
            let sequence = (this._request_sequence_index++).toString();
            sequence += "-";
            for (let i = 0; i < 8; ++i) {
                sequence += krequest_sequence_chars.charAt(parseInt((Math.random() * krequest_sequence_chars.length).toString()));
            }
            return sequence;
        }

        private _request_service<TResult>(message: IRequestMessage<any>, first_handleshake?: boolean): Promise<IResponseMessage<TResult>> {
            return new Promise<IResponseMessage<any>>((resolve, reject) => {
                const timeout = this.requestTimeout;

                if (this.connected || first_handleshake) {
                    const data = JSON.stringify(message);
                    const timeout_timer = setTimeout(function () {
                        reject({
                            ec: ErrorCodes.Timeout,
                            message: "request timeout."
                        });
                    }, timeout);

                    this._request_transactions.push(message.sequence, {
                        resolve: function (response) {
                            clearTimeout(timeout_timer);
                            resolve(response);
                        },
                        reject: function (ec, message) {
                            clearTimeout(timeout_timer);
                            reject({
                                ec: ec,
                                message: message
                            });
                        }
                    });
                    if (this._socket)
                        this._socket.send(new UTF8Encoder().encode(data));
                    else {
                        clearTimeout(timeout_timer);
                        reject({
                            ec: ErrorCodes.NotReady,
                            message: message
                        });
                    }
                } else {
                    reject({
                        ec: ErrorCodes.NotReady,
                        message: message
                    });
                }
            });
        }

        private _make_request_message<TArgs>(cmd: string, args?: TArgs): IRequestMessage<TArgs> {
            if (typeof (args) === "undefined")
                args = {} as TArgs;

            return {
                cmd: cmd,
                sequence: this._generate_request_sequence_string(),
                timestamp: new Date().getTime(),
                args: args
            };
        }

        private _websocket_reconnect(): void {
            if (this._user_closed)
                return;

            const timeout = this._reconnect_timeout;
            this._reconnect_timeout += (this._reconnect_timeout + 500) / 2;
            if (this._reconnect_timeout > kmax_reconnect_interval)
                this._reconnect_timeout = kmax_reconnect_interval;

            setTimeout(() => {
                if (this._user_closed)
                    return;
                this._socket = this._websocket_init();
            }, timeout);
        }

        private _websocket_init(): WebSocket {
            console.log('this._wsBaseUrl:', this._wsBaseUrl);
            const wsUrl = `${this._wsBaseUrl}${websocket_service_url}`
            console.log('this.wsUrl:', wsUrl);
            let utf8Decoder = new UTF8Decoder();
            let utf8Encoder = new UTF8Encoder();
            let ws = new WebSocket(
                wsUrl,
                [...protocols]
            );
            ws.binaryType = "arraybuffer";
            ws.addEventListener("open", async (ev) => {
                let message = this._make_request_message("HandleshakeRequest");
                this._protocol = ws.protocol;
                let response = await this._request_service<ServiceInfo>(message, true);
                this._connected = true;
                this._reconnect_timeout = kmin_reconnect_interval;

                this._heartbeatTimer = setInterval(() => {
                    if (this.connected && this._socket) {
                        let m = this._make_request_message("HeartbeatRequest");
                        m.sequence = "HeartbeatRequest";
                        this._socket.send(utf8Encoder.encode(JSON.stringify(m)));
                    }
                }, 15000);
                const event = new ServiceConnectedEvent();
                event.initEvent("ServiceConnected");
                event.serverInfo = response.result;
                this.dispatchEvent(event)
            });

            ws.addEventListener("close", (ev) => {
                this._connected = false;
                clearInterval(this._heartbeatTimer);
                let trans = this._request_transactions.popAll();
                for (const t of trans) {
                    try {
                        t.reject(ErrorCodes.ConnectionDisconnected, "Connection close.");
                    } catch (ex) {

                    }
                }

                let error;
                try {
                    const event = new ServiceEvent();
                    event.initEvent("ServiceDisconnected");
                    this.dispatchEvent(event)
                } catch (ex) {
                    error = ex;
                }
                this._websocket_reconnect();
                if (error)
                    throw error;
            });

            ws.addEventListener("error", (ev) => {
                ev.preventDefault();
                if (ev.stopImmediatePropagation)
                    ev.stopImmediatePropagation();
                return false;
            });

            ws.addEventListener("message", (ev) => {
                const message = JSON.parse(utf8Decoder.decode(ev.data)) as IResponseMessage<any>;
                if (message.cmd) {
                    switch (message.cmd) {
                        case "ChannelDisconnected": {
                            const event = new ChannelStatusEvent();
                            event.initEvent("ChannelDisconnected");
                            event.channelId = message.result.id;
                            event.connectCode = message.result.code;
                            this.dispatchEvent(event);
                            break;
                        }
                        case "LogOutput": {
                            const event = new LogOutputEvent();
                            event.initEvent("LogOutput");
                            event.level = message.result.level;
                            event.source = message.result.source;
                            event.message = message.result.message;
                            this.dispatchEvent(event);
                            break;
                        }
                        case "HeartbeatResponse": {
                            if (message.sequence === "HeartbeatRequest") {
                                const event = new HeartbeatMessageEvent();
                                event.initEvent("HeartbeatMessage");
                                event.data = message.result;
                                this.dispatchEvent(event);
                            } else {
                                if (message.sequence) {
                                    const handler = this._request_transactions.tryPop(message.sequence);
                                    if (handler)
                                        handler.resolve(message);
                                }
                            }
                            break;
                        }
                        case "ChannelDiagnosisResponse": {
                            if (message.sequence && message.result) {
                                const handler = message.result.finish ?
                                    this._request_transactions.tryPop(message.sequence) :
                                    this._request_transactions.tryPeek(message.sequence);

                                if (handler)
                                    handler.resolve(message);
                            }
                            break;
                        }
                        default: {
                            if (message.sequence) {
                                const handler = this._request_transactions.tryPop(message.sequence);
                                if (handler)
                                    handler.resolve(message);
                            }
                            break;
                        }
                    }
                }
            });
            return ws;
        }
    }
}
